import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../../hooks/useApi';
import { listUsers } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { Button } from '../../components/Button';
import { NewUserModal } from './NewUserModal';
import { formatLocal } from '../../lib/time';
import { fullName } from '../../lib/format';
import { PS, T } from '../../lib/theme';
import type { AdminLookupUser, Paged } from '../../lib/types';
import { compareDateDescending } from '../../lib/admin-list-order';

const PAGE_SIZE = 50;

export function UsersPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const offset = Number(params.get('offset') ?? 0);

  const [draftQ, setDraftQ] = useState(q);
  const [newOpen, setNewOpen] = useState(false);

  const loader = useCallback(
    (signal: AbortSignal) => listUsers({ q, limit: PAGE_SIZE, offset }, signal),
    [q, offset],
  );
  const { data, error, loading } = useApi<Paged<AdminLookupUser>>(loader);

  const columns = useMemo<ColumnDef<AdminLookupUser, unknown>[]>(() => [
    {
      header: 'Name',
      id: 'name',
      accessorFn: (u) => fullName(u),
    },
    {
      header: 'Phone',
      accessorKey: 'phone',
      cell: (info) => info.getValue<string | null>() ?? '—',
    },
    {
      header: 'Email',
      accessorKey: 'email',
      cell: (info) => info.getValue<string | null>() ?? '—',
    },
    {
      header: 'Credit',
      accessorKey: 'credit_minutes',
      cell: (info) => `${info.getValue<number>()} min`,
    },
    {
      header: 'Flags',
      id: 'flags',
      cell: (info) => {
        const u = info.row.original;
        const tags: string[] = [];
        if (u.is_admin) tags.push('admin');
        if (u.is_coach) tags.push('coach');
        return tags.length ? tags.join(', ') : '—';
      },
    },
    {
      header: 'Joined',
      accessorKey: 'created_at',
      cell: (info) => formatLocal(info.getValue<string>(), 'yyyy-MM-dd'),
      sortingFn: (left, right) => -compareDateDescending(
        { date: left.original.created_at, id: left.original.id },
        { date: right.original.created_at, id: right.original.id },
      ),
      sortUndefined: 'last',
    },
    {
      header: 'Telegram',
      id: 'telegram',
      accessorFn: (row) => row.telegram_id ? String(row.telegram_id) : '—',
    },
  ], []);

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({ q: draftQ, offset: '0' });
  };

  const onPage = (nextOffset: number) => {
    setParams({ q, offset: String(nextOffset) });
  };

  const total = data?.total ?? 0;
  const showingFrom = data && data.items.length > 0 ? offset + 1 : 0;
  const showingTo = data ? Math.min(offset + data.items.length, total) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Users</h1>
        <form onSubmit={onSearchSubmit} style={{ display: 'flex', gap: 8, flex: '1 1 auto' }}>
          <input
            type="search"
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
            placeholder="Search by name, phone, or email"
            style={searchInput}
          />
          <Button type="submit">Search</Button>
          {q && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setDraftQ(''); setParams({}); }}
            >
              Clear
            </Button>
          )}
        </form>
        <Button onClick={() => setNewOpen(true)}>New user</Button>
      </header>

      {loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
      {error && (
        <div style={errBanner}>{error.message}</div>
      )}

      {!loading && !error && (
        <>
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            emptyLabel={q ? 'No users match this search.' : 'No users yet.'}
            onRowClick={(row) => navigate(`/users/${row.id}`)}
            getRowLabel={(row) => `Open user ${fullName(row)}`}
            // The endpoint already provides global chronology; this simply
            // makes that server-owned default visible without collecting pages.
            initialSort={[{ id: 'created_at', desc: true }]}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...T.caption, color: PS.textSec }}>
              {data ? `Showing ${showingFrom}–${showingTo} of ${total}` : ''}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="secondary"
                disabled={offset === 0}
                onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </Button>
              <Button
                variant="secondary"
                disabled={!data || offset + data.items.length >= total}
                onClick={() => onPage(offset + PAGE_SIZE)}
              >
                Next →
              </Button>
            </div>
          </div>
        </>
      )}

      <NewUserModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}

const searchInput: CSSProperties = {
  flex: '1 1 240px', minWidth: 200,
  padding: '8px 10px', minHeight: 40, borderRadius: 3,
  border: `1px solid ${PS.border}`,
  fontFamily: "'Barlow',sans-serif", fontSize: 16,
};
const errBanner: CSSProperties = {
  padding: 12, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`,
  color: PS.errorText, borderRadius: 3,
};
